(ns app.domain.review.model
  (:require #?(:clj  [app.time :as time]
               :cljs [core.front.date-util :as date-util])
            #?(:clj  [core.feature-flag.core :as feature-flag]
               :cljs [core.front.feature-flags :as ff])
            #?(:clj  [datomic.api :as d])
            [app.date :as date]
            [app.domain.acceptance.model :as acceptance-model]
            [app.domain.application.shared-core :as shared-core]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.blackout.model :as blackout-model]
            [app.domain.group.model :as group-model]
            [app.domain.party.model :as party-model]
            [app.domain.target-time.model :as target-time-model]
            [app.domain.util :as du]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [clojure.set :as set]
            [core.db.child-entity :as child]
            [core.user.model :as user-model]
            [potpuri.core :as p]
            [schema.core :as s]))


(def reminder-due-days
  "Days before falling due notification is sent"
  3)


(def review-task-types
  #{:task.type/review
    :task.type/new-review-round})


(defn review-task-type? [task-type]
  (contains? review-task-types task-type))


(def ^{:arglists '([review-config lang])} review-config-name
  (i18n-model/translation-fallback-fn :review.config/name))


(defn review-name [review language]
  (review-config-name (:review/review-config review) language))


(defn application [review]
  (-> review :entity/parent))


(defn workspace [review]
  (-> review application :entity/parent))


(def ^{:arglists '([application])} application-reviews
  (child/child-entities-of-type-fn :entity.type/review))

(defn application-reviews-for-current-step [application]
  (->> (application-reviews application)
       (filter #(shared-core/review-created-in-current-step? application %))))


(def ^{:arglists '([review])} sub-reviews
  "All review rounds of a review, in creation order."
  (comp (partial sort-by :entity/created)
        (child/child-entities-of-type-fn :entity.type/sub-review)))


(defn current-sub-review
  "The review round currently being worked on, i.e. the latest one."
  [review]
  (last (sub-reviews review)))

(defn own-changes-requested?
  "Whether the current review round has actionable changes requested by this review.
   Only the latest acceptance for each current-round item is considered; comments are
   considered part of the review's own change request."
  [review]
  (let [sub-review                 (current-sub-review review)
        round-acceptances          (:sub-review/acceptances sub-review)
        acceptances                (->> round-acceptances
                                        (group-by (comp :db/id acceptance-model/acceptance-parent))
                                        vals
                                        (map du/latest-entity))
        application                (application review)
        requirements               (:review/requirements review)
        requested-attachment-types (->> requirements
                                        (keep #(-> % :requirement/attachment-type :db/id))
                                        set)
        attachment-types           (->> (attachment-model/phase-attachments application)
                                        (mapcat :attachment/types)
                                        (keep :db/id)
                                        set)
        requested-party-roles      (->> requirements
                                        (keep #(-> % :requirement/party-role :db/id))
                                        set)
        available-party-roles      (->> (party-model/active-parties application)
                                        (mapcat :party/party-roles)
                                        (keep :db/id)
                                        set)]
    (or (not (rich-text/empty-html-rich-text? (:sub-review/comment sub-review)))
        (some #(and (du/in-state? :acceptance.state/active %)
                    (= :acceptance.value/needs-changes (:acceptance/value %)))
              acceptances)
        (seq (set/difference requested-attachment-types attachment-types))
        (seq (set/difference requested-party-roles available-party-roles)))))

(defn- review-order [review]
  (or (:ordered/order review)
      #?(:clj Long/MAX_VALUE
         :cljs js/Number.MAX_SAFE_INTEGER)))

(defn previous-unfinished-application-reviews [review all-reviews]
  (filter #(and (< (review-order %)
                   (review-order review))
                (-> % :review/end nil?))
          all-reviews))


(defn created-in-state-for-review
  ([application]
   (created-in-state-for-review application nil))
  ([application created-in-state]
   (or (shared-core/normalized-review-created-state created-in-state)
       (some-> application :process/state shared-core/normalized-review-created-state)
       :process.state/in-review)))


(defn next-assigned-taskless-review [review application]
  (let [all-reviews        (application-reviews-for-current-step application)
        taskless-candidate (p/find-first
                             (sort-by review-order all-reviews)
                             #(and (> (review-order %) (review-order review))
                                   (-> % :review/end nil?)
                                   (or (:review/assignee %)
                                       (:review/assigned-to-group %))
                                   (-> % :task/_reference nil?)))]
    (when (and taskless-candidate
               (->> all-reviews
                    (previous-unfinished-application-reviews taskless-candidate)
                    (remove #(= (review-order %) (review-order review)))
                    empty?))
      taskless-candidate)))


(defn phase-reviewer-names [phase]
  (let [sub-reviews (->> (application-reviews phase)
                         (mapcat :entity/_parent))
        reviewers   (->> sub-reviews
                         (filter #(-> % :review/status (= :review.status/approved)))
                         (map :sub-review/reviewer)
                         distinct)]
    (->> reviewers
         (sort-by :user/last-name)
         (map user-model/full-name))))


(s/defn reviews-with-matching-requirements
  "Find open reviews, whose requirements refer to one or more `target-ids`
   through the given attribute."
  [app
   review-attr :- (s/enum :requirement/attachment-type
                          :requirement/party-role)
   target-ids :- #{s/Int}]
  (->> (application-reviews app)
       (filter (fn [{:review/keys [status requirements]}]
                 (and
                   (not= status :review.status/approved)
                   (seq (set/intersection target-ids
                                          (->> requirements
                                               (map (comp :db/id review-attr))
                                               set))))))))

(def review-can-get-acceptance? (comp boolean #{:review.status/in-review :review.status/changes-requested} :review/status))

(def not-yet-begun-statuses
  "Review statuses where the review has not been started yet, so it may still be assigned to an
   assignment group (rather than an individual reviewer)."
  #{:review.status/draft :review.status/unreviewed})

(defn not-yet-begun?
  "True when the review has not been started yet, i.e. it is still a draft or unreviewed."
  [review]
  (contains? not-yet-begun-statuses (:review/status review)))

(defn is-reviewer? [auth-group user]
  (boolean
    (some (fn [group]
            (and (group-model/has-role? :group.role/reviewer group)
                 (or (du/is-descendant? auth-group group)
                     (when-let [superdomain (group-model/group-superdomain auth-group)]
                       (du/is-descendant? superdomain group)))))
          (:user/groups user))))

(defn is-assignment-group-of?
  "Review assignment groups are child groups of auth-group's plans reviewers group"
  [authority-group-id group]
  (and (group-model/review-assignment-group? group)
       (some-> group :entity/parent :entity/parent :db/id (= authority-group-id))))

(defn is-member-of-assignment-group?
  [assignment-group assignee]
  (some->> assignment-group
           group-model/get-users-in-group
           (some #(= (:db/id assignee) (:db/id %)))))

(defn begin-review-permitted?
  "True when `user` is allowed to begin `review`: unassigned reviews are claimable by any
   permitted reviewer, also individually assigned reviews. Assignment-group
   reviews only by members of that group."
  [review assignment-groups-enabled? user]
  (or
    (not assignment-groups-enabled?)
    (cond
      (:review/assigned-to-group review)
      (-> review
          :review/assigned-to-group
          (is-member-of-assignment-group? user))

      :else true)))

(defn due-date-default-fields-to-clear
  "Default calculation attributes to remove when the review config is in manual mode."
  [review-config]
  (when-not (:review.config/due-date-auto? review-config)
    [:review.config/due-date-default-value
     :review.config/due-date-default-unit]))

(defn due-date-enabled?
  "Whether due dates are enabled for this config, authority group and environment."
  [review-config authority-group feature-flags]
  (and (contains? feature-flags :feature-flag.id/review-due-date)
       (group-model/has-feature? authority-group :group.feature/review-due-date)
       (boolean (:review.config/due-date-enabled? review-config))))

(defn automatic-due-date?
  "Whether due dates are enabled and automatic mode is selected, regardless of calculation inputs."
  [review-config authority-group feature-flags]
  (and (due-date-enabled? review-config authority-group feature-flags)
       (boolean (:review.config/due-date-auto? review-config))))

(defn manual-due-date?
  "Whether due dates are enabled and manual mode is selected."
  [review-config authority-group feature-flags]
  (and (due-date-enabled? review-config authority-group feature-flags)
       (not (:review.config/due-date-auto? review-config))))

(defn due-date-calc-required-and-configured?
  "True when a review config is set to calculate due dates automatically. Auto mode requires the master
   due-date toggle to be on, the auto flag set, and both the default value and unit present so a date can
   actually be computed. Feature-flag and group-feature gating are checked separately at call sites."
  [review-config]
  (boolean
    (and (:review.config/due-date-enabled? review-config)
         (:review.config/due-date-auto? review-config)
         (:review.config/due-date-default-value review-config)
         (:review.config/due-date-default-unit review-config))))

(def default-due-date-round-policy :review.due-date-round-policy/continue)

(def review-due-date-round-policies
  #{:review.due-date-round-policy/recalculate
    :review.due-date-round-policy/pause-and-resume
    :review.due-date-round-policy/continue})

(defn due-date-round-policy [authority-group]
  (:group/review-due-date-round-policy authority-group default-due-date-round-policy))

(defn remaining-due-days
  "Count calendar days remaining through the review's local due date."
  [review authority-group now]
  (when-let [due-date (:review/due-date review)]
    (let [timezone (:group/tz authority-group)]
      (date/count-days (date/local-date now timezone)
                       (date/local-date due-date timezone)))))

(defn remaining-due-business-days
  "Count business days remaining through the review's local due date."
  [review authority-group now]
  (when-let [due-date (:review/due-date review)]
    (let [timezone  (:group/tz authority-group)
          blackouts (-> (blackout-model/current-group-general-blackouts
                          authority-group
                          (if (< (target-time-model/inst->long due-date)
                                 (target-time-model/inst->long now))
                            due-date
                            now))
                        (blackout-model/localize-blackouts timezone))]
      (target-time-model/count-due-days
        {:start-date (date/previous-local-day-marker now timezone)
         :end-date   (date/local-day-marker due-date timezone)
         :unit       :target-time.unit/business-days
         :blackouts  blackouts
         :timezone   timezone}))))

(defn calculate-due-date
  "Calculate due date for a review based on its review config's default value/unit.
    Uses a paused review's stored remaining days in the currently configured unit when present.
    Returns a Date or nil if config has no default."
  [review review-config auth-group now]
  (let [value          (:review.config/due-date-default-value review-config)
        unit           (:review.config/due-date-default-unit review-config)
        remaining-days (case unit
                         :target-time.unit/business-days (:review/remaining-due-business-days review)
                         :target-time.unit/calendar-days (:review/remaining-due-days review)
                         nil)
        paused?        (some? remaining-days)
        days           (if paused? remaining-days value)
        feature-flag   :feature-flag.id/review-due-date]
    (when (and #?(:clj  (feature-flag/flag-enabled? (d/entity-db auth-group) feature-flag)
                  :cljs (ff/enabled? feature-flag))
               days unit
               (group-model/has-feature? auth-group :group.feature/review-due-date)
               (:review.config/due-date-enabled? review-config))
      (let [timezone  (:group/tz auth-group)
            blackouts (when (= unit :target-time.unit/business-days)
                        (blackout-model/current-group-general-blackouts auth-group now))
            due-date  (target-time-model/calculate-due-date
                        (p/assoc-if {:start-date               now
                                     :days                     days
                                     :unit                     unit
                                     :timezone                 timezone
                                     :business-day-end-time    target-time-model/business-day-end-time-default
                                     :calculation-starting-day :target-time.starting-day/current-day}
                                    :blackouts (when (= unit :target-time.unit/business-days)
                                                 blackouts)))
            ;; Moving a negative value forward to the next business day could make an overdue review due today.
            due-date  (if (and paused?
                               (not (neg? days))
                               (= unit :target-time.unit/business-days))
                        (target-time-model/today-or-next-business-day due-date timezone blackouts)
                        due-date)]
        #?(:clj  (time/end-of-day-in-tz due-date timezone)
           :cljs (date-util/end-of-day-in-tz due-date timezone))))))

(defn final-review-id [review]
  (some-> review :review/final-review :db/id))

(defn final-review? [review]
  (-> review :review/review-config :review.config/final-review? boolean))

(defn reviews-linked-to-final [final-review]
  (:review/_final-review final-review))

(defn final-review-ready?
  ([final-review]
   (final-review-ready? final-review (reviews-linked-to-final final-review)))
  ([final-review linked-reviews]
   (or (nil? final-review)
       (some? (:review/end final-review))
       (empty? linked-reviews)
       (every? :review/end linked-reviews))))

(defn prevent-task-creation?
  "Whether review precedence currently prevents creating its task."
  [review application {:review.config/keys [create-task-after-previous-reviews-finished? final-review?]}
   force-task-precedence?]
  (or (and final-review?
           (not (final-review-ready? review)))
      (and create-task-after-previous-reviews-finished?
           (or force-task-precedence?
               (seq (previous-unfinished-application-reviews
                      review
                      (application-reviews-for-current-step application)))))))

(def completed-review-statuses
  #{:review.status/approved :review.status/changes-requested})

(defn final-review-released? [final-review]
  (contains? completed-review-statuses (:review/status final-review)))

(defn linked-to-incomplete-final-review? [review]
  (some-> review :review/final-review final-review-released? not))

(defn held-review? [review]
  (and (contains? completed-review-statuses (:review/status review))
       (linked-to-incomplete-final-review? review)))

(defn held-reviews [final-review]
  (filter held-review? (reviews-linked-to-final final-review)))

(defn all-linked-reviews-approved? [final-review]
  (every? #(= :review.status/approved (:review/status %))
          (reviews-linked-to-final final-review)))

(defn all-linked-review-changes-done?
  "Whether no linked review, except an optionally excluded review, has requested changes."
  ([final-review]
   (all-linked-review-changes-done? final-review nil))
  ([final-review excluded-review-id]
   (or (not (final-review? final-review))
       (->> final-review
            reviews-linked-to-final
            (remove #(= excluded-review-id (:db/id %)))
            (every? #(not= :review.status/changes-requested (:review/status %)))))))

(defn applicant-review-visible?
  "Whether a released change-request review, filtered upstream, needs its applicant section.
   Keep final reviews actionable when linked changes are done, even without own requests."
  [review]
  (or (not (final-review? review))
      (own-changes-requested? review)
      (all-linked-review-changes-done? review)))

(defn all-linked-reviews-completed? [final-review]
  (every? #(contains? completed-review-statuses (:review/status %))
          (reviews-linked-to-final final-review)))
